import * as React from "react";
import { useParams } from "react-router-dom";
import Loader from "../Components/Loader";
import { ErrorCard } from "../Components/ErrorCard";
import { apiPublicRawGet } from "../DataService/apiClient";
import "./FreebieClaimPage.css";

export const FreebieDownloadPage = () => {
  const { downloadToken } = useParams<{ downloadToken: string }>();
  const [error, setError] = React.useState<string | null>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [hasDownloaded, setHasDownloaded] = React.useState(false);

  const getErrorMessage = async (response: Response, fallback: string) => {
    const contentType = response.headers.get("content-type") ?? "";

    if (contentType.includes("application/json")) {
      const payload = await response.json().catch(() => null);
      if (payload && typeof payload === "object") {
        const maybeMessage =
          (payload as { message?: unknown; title?: unknown; error?: unknown }).message ??
          (payload as { message?: unknown; title?: unknown; error?: unknown }).title ??
          (payload as { message?: unknown; title?: unknown; error?: unknown }).error;

        if (typeof maybeMessage === "string" && maybeMessage.trim()) {
          return maybeMessage;
        }
      }
    }

    const text = await response.text().catch(() => "");
    if (text.trim()) return text.trim();

    if (response.status === 410) {
      return "Länken är inte längre giltig eller har redan använts.";
    }

    if (response.status === 404) {
      return "Vi kunde inte hitta din gåva.";
    }

    return fallback;
  };

  const handleDownload = async () => {
    if (!downloadToken) {
      setError("Ogiltig nedladdningslänk.");
      return;
    }

    setIsDownloading(true);

    try {
      const response = await apiPublicRawGet(`/api/freebies/download/${downloadToken}`);

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, `Nedladdningen misslyckades (${response.status}).`)
        );
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const fileName =
        response.headers
          .get("content-disposition")
          ?.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)?.[1]
          ?.replace(/"/g, "")
          .trim() || "freebie";

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      setHasDownloaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunde inte ladda ner gåvan.");
    } finally {
      setIsDownloading(false);
    }
  };

  if (error) {
    return (
      <div className="freebie-claim-page">
        <div className="freebie-claim-shell">
          <ErrorCard message={error} />
        </div>
      </div>
    );
  }

  return (
    <div className="freebie-claim-page">
      <div className="freebie-claim-shell">
        <div className="freebie-state-card freebie-state-card--success">
          <p className="freebie-state-card__kicker">Din gåva</p>
          <h2>Ladda ner</h2>

          <p className="freebie-state-card__body">
            Klicka på knappen nedan för att hämta din gåva.
          </p>

          <button
            type="button"
            className="freebie-state-card__button"
            onClick={() => void handleDownload()}
            disabled={isDownloading || hasDownloaded}
          >
            {isDownloading ? "Laddar ner..." : hasDownloaded ? "Nedladdning startad" : "Ladda ner gåvan"}
          </button>

          {hasDownloaded && (
            <p className="freebie-state-card__note">
              Om nedladdningen inte startade automatiskt, testa att klicka igen.
            </p>
          )}

          <p className="freebie-state-card__note">
            Om du har frågor är du varmt välkommen att höra av dig till Sandras Hälsa.
          </p>
        </div>

        {isDownloading && <Loader loaderSize="MEDIUM" />}
      </div>
    </div>
  );
};

export default FreebieDownloadPage;
